"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { api } from "@/lib/api"
import { useWaitingConversationStore } from "@/stores/waitingConversationStore"
import { THREAD_EVENTS_PAGE_SIZE } from "@/components/chat/constants/chatInterface.constants"
import {
    latestWilsonAnswerMarkdown,
    waitingConversationStatus,
    waitingQuestionText,
} from "@/components/chat/utils/waitingConversation.utils"

const RUN_POLL_MS = 4_000

export interface WaitingAnswer {
    threadId: string
    question: string
    answer: string | null
    isWorking: boolean
}

export function useWaitingAnswer({ isEnabled }: { isEnabled: boolean }): {
    waitingAnswer: WaitingAnswer | null
    dismiss: () => void
} {
    const waiting = useWaitingConversationStore(state => state.waiting)
    const dismiss = useWaitingConversationStore(state => state.dismiss)
    const isPollingRef = useRef(false)
    const pollInterval = () => (isPollingRef.current ? RUN_POLL_MS : false)

    const isWatching = isEnabled && !!waiting?.isWorkingAtClose
    const threadId = waiting?.threadId ?? ""

    const activeRun = api.chat.getActiveRun.useQuery(
        { threadId },
        { enabled: isWatching, staleTime: 0, refetchInterval: pollInterval }
    )
    const events = api.chat.getThreadEvents.useQuery(
        { threadId, limit: THREAD_EVENTS_PAGE_SIZE, offset: 0 },
        { enabled: isWatching, staleTime: 0, refetchInterval: pollInterval }
    )
    const closedAt = waiting?.closedAt ?? 0
    const isRunGone =
        activeRun.data === null && activeRun.dataUpdatedAt >= closedAt
    const [runGoneSeenAt, setRunGoneSeenAt] = useState<number | null>(null)
    useEffect(() => {
        setRunGoneSeenAt(seenAt => (isRunGone ? (seenAt ?? Date.now()) : null))
    }, [isRunGone])
    const refetchEvents = events.refetch
    useEffect(() => {
        if (runGoneSeenAt !== null) void refetchEvents()
    }, [runGoneSeenAt, refetchEvents])
    const freshEvents =
        events.dataUpdatedAt >= closedAt ? events.data?.events : undefined
    const status = waitingConversationStatus({
        isWorkingAtClose: waiting?.isWorkingAtClose ?? false,
        activeRun:
            activeRun.dataUpdatedAt >= closedAt ? activeRun.data : undefined,
        hasRunCheckFailed: activeRun.isError,
        runCheckedMsAfterClose: activeRun.dataUpdatedAt - closedAt,
        hasEventsSinceRunGone:
            runGoneSeenAt !== null && events.dataUpdatedAt > runGoneSeenAt,
        events: freshEvents,
        closedAt,
        questionAtClose: waiting?.question,
    })
    useEffect(() => {
        isPollingRef.current = isWatching && status === "working"
    })

    const waitingAnswer = useMemo((): WaitingAnswer | null => {
        if (!isWatching || !waiting || status === "idle") return null
        const shown = events.data?.events
        const question =
            (shown && waitingQuestionText(shown, closedAt)) ||
            waiting.question ||
            waiting.title ||
            ""
        if (!question) return null
        return {
            threadId: waiting.threadId,
            question,
            answer:
                status === "replied" && freshEvents
                    ? latestWilsonAnswerMarkdown(freshEvents)
                    : null,
            isWorking: status === "working",
        }
    }, [isWatching, waiting, status, events.data, freshEvents, closedAt])

    useEffect(() => {
        if (waiting && !waiting.isWorkingAtClose) dismiss()
    }, [waiting, dismiss])

    return { waitingAnswer, dismiss }
}
